import { FF } from 'modules/feature-flags';
import { useSplitFlag } from 'modules/feature-flags/useSplitFlag';
import { logger } from 'modules/logger';
import { useLayouts } from '../Viewer/StudyLoader/useLayouts';
import { DEFAULT_LAYOUT } from '../Viewer/StudyLoader/viewerLoaderState';
import { generateViewportId, getViewportGrid } from '../Viewer/viewerUtils';

import { useVirtualMonitorSplitToggle } from 'hooks/useVirtualMonitorSplitToggle';

import { useRecoilState, useRecoilValue } from 'recoil';
import { viewportUpIdSelector, viewportUpIds } from '../Viewer/viewerState';
import { WithApolloContext } from 'modules/Apollo/types';

export const useRenderedViewportIds = ({ context }: WithApolloContext): Array<string> => {
  const viewportIds = useRecoilValue(viewportUpIds);
  const [virtualMonitorSplit] = useVirtualMonitorSplitToggle({ context });
  const { layoutsForCase: layouts } = useLayouts({ context });
  const [minColumnsToEnableMultipleExpansionAreas] = useSplitFlag(
    FF.MIN_COLUMNS_TO_ENABLE_MULTIPLE_EXPANSION_AREAS,
    (value) => Number(value ?? 2)
  );

  return Object.entries(layouts).flatMap(([windowId, layout]: [any, any]) => {
    return Array.from(
      new Set(
        getViewportGrid(
          viewportIds[windowId] ?? [],
          (row, col) => generateViewportId(windowId, row, col),
          layout,
          virtualMonitorSplit,
          minColumnsToEnableMultipleExpansionAreas
        ).flat()
      )
    );
  });
};

export const useRenderedViewportIdsForWindow = ({
  windowId,
  context,
}: { windowId: string } & WithApolloContext): Array<string> => {
  const [viewportIds] = useRecoilState(viewportUpIdSelector(windowId));
  const [virtualMonitorSplit] = useVirtualMonitorSplitToggle({ context });
  const { layoutForWindow: layout } = useLayouts({ context });
  const [minColumnsToEnableMultipleExpansionAreas] = useSplitFlag(
    FF.MIN_COLUMNS_TO_ENABLE_MULTIPLE_EXPANSION_AREAS,
    (value) => Number(value ?? 2)
  );

  // A missing layout for the window we are actively rendering is a bug state, not a legitimate
  // "nothing to show". `[0, 0]` here rendered an empty reading area -- no viewports, no error, no log
  // -- which is how VX-4764's blank-viewport reports reached QA and staging without a trace to follow.
  // useLayouts now keeps the current window's layout even when the extension has not reported it, so
  // this should be unreachable; it stays as a floor because a blank viewer is the worst possible
  // failure mode here, and it logs so the next occurrence is visible instead of silent.
  if (layout == null) {
    logger.warn('[viewer] no layout resolved for the rendering window; falling back to default', {
      windowId,
    });
  }

  return Array.from(
    new Set(
      getViewportGrid(
        viewportIds,
        (row, col) => generateViewportId(windowId, row, col),
        layout ?? DEFAULT_LAYOUT,
        virtualMonitorSplit,
        minColumnsToEnableMultipleExpansionAreas
      ).flat()
    )
  );
};
